import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError, API } from "@/contexts/AuthContext";
import { AuthShell, TextField } from "@/pages/Login";
import { UserPlus, EnvelopeOpen } from "@phosphor-icons/react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || "";

  const [invite, setInvite] = useState(null); // { company_name, role, is_valid }
  const [inviteLoading, setInviteLoading] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    if (!inviteToken) {
      setInvite(null);
      return;
    }
    setInviteLoading(true);
    axios
      .get(`${API}/invitations/${inviteToken}`)
      .then(({ data }) => !cancel && setInvite(data))
      .catch(() => !cancel && setInvite({ is_valid: false }))
      .finally(() => !cancel && setInviteLoading(false));
    return () => {
      cancel = true;
    };
  }, [inviteToken]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await register({
        name,
        email,
        password,
        company_name: inviteToken ? null : companyName || null,
        invite_token: inviteToken || null,
      });
      toast.success("Konto erstellt, willkommen bei Plantus");
      // Bei neuen Eigentuemern: Onboarding mit Import-Wizard anbieten.
      // Bei Invite-Mitgliedern: direkt ins Dashboard.
      const isNewOwner = u?.role === "owner" && !inviteToken;
      navigate(isNewOwner ? "/willkommen" : "/app", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-forest-900">
        Konto erstellen
      </h1>
      <p className="mt-2 font-body text-[15px] text-[#4A524C]">
        Mit den hier gewählten Daten melden Sie sich auch in der Plantus-App an.
      </p>

      {inviteToken && (
        <div
          data-testid="invite-info"
          className={`mt-6 rounded-2xl border p-4 flex items-start gap-3 ${
            invite?.is_valid
              ? "bg-forest-50 border-forest-100"
              : "bg-red-50 border-red-200"
          }`}
        >
          <EnvelopeOpen
            size={22}
            weight="duotone"
            className={invite?.is_valid ? "text-forest-700" : "text-red-600"}
          />
          <div className="text-sm leading-relaxed">
            {inviteLoading ? (
              <span className="text-[#4A524C]">Einladung wird geprüft …</span>
            ) : invite?.is_valid ? (
              <>
                <strong className="text-forest-900">
                  Einladung von „{invite.company_name}"
                </strong>
                <div className="text-[#4A524C]">
                  Sie werden als Mitarbeiter zu diesem Betrieb hinzugefügt.
                </div>
              </>
            ) : (
              <span className="text-red-700">
                Einladungslink ist ungültig oder abgelaufen. Sie können sich
                trotzdem mit einem eigenen Betrieb registrieren.
              </span>
            )}
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} data-testid="register-form" className="mt-6 space-y-5">
        <TextField
          label="Ihr Name"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          testId="register-name-input"
        />
        <TextField
          label="E-Mail"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          testId="register-email-input"
        />
        <TextField
          label="Passwort (min. 8 Zeichen)"
          name="password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          testId="register-password-input"
        />
        {!inviteToken || !invite?.is_valid ? (
          <TextField
            label="Name des Betriebs (optional)"
            name="companyName"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="z. B. Hof Müller"
            testId="register-company-input"
          />
        ) : null}
        {error && (
          <div
            data-testid="register-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit-button"
          className="w-full inline-flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white font-heading font-medium px-6 py-3 rounded-xl transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
        >
          <UserPlus size={18} weight="bold" />
          {loading ? "Erstelle Konto …" : "Konto erstellen"}
        </button>
      </form>

      <p className="mt-7 font-body text-sm text-[#4A524C]">
        Schon ein Konto?{" "}
        <Link
          to="/login"
          data-testid="register-go-login"
          className="text-forest-700 font-medium hover:underline"
        >
          Jetzt anmelden
        </Link>
      </p>
    </AuthShell>
  );
}
